import moment from "moment";
import { useEffect, useState } from "react";
import { getLoans } from "service/policy/getLoans";
import { Trans, useTranslation } from "react-i18next";
import { stringFormattingToCurrency } from "js/formatting";
import { getFinancials } from "service/policy/getFinancials";
import {
  isDeferredAnnuityPlanCode,
  isOldAlicUlPolicy,
  isWholeLifePolicy,
} from "js/utilities";

import Tooltip from "components/General/Tooltip";
import CardWithFields from "components/General/CardWithFields";
import ContactUsDialog from "components/ModalDialogs/ContactUsDialog";
import CardDisclaimerBase from "components/General/CardDisclaimerBase";

export default function PolicyValue(props) {
  const { t } = useTranslation();
  const { data } = props;
  const { sideFund, dividendAccumulation } = data?.cashValue;
  const [contactUsDialogState, setContactUsDialogState] = useState(false);
  const [loans, setLoans] = useState({});
  const [maxLoanAmt, setMaxLoanAmt] = useState("");
  const [cashValue, setCashValue] = useState();
  const isDeferredAnnuityPlanCodeExists = isDeferredAnnuityPlanCode(data);

  useEffect(() => {
    getLoans(data?.contractID).then((loanDetails) => {
      setLoans(loanDetails);
      const productType = data?.product?.type?.toUpperCase();
      if (isWholeLifePolicy(data?.product?.type)) {
        setMaxLoanAmt(loanDetails?.wlLoans?.amount);
      } else if (productType === "LQ" || productType === "LZ") {
        setMaxLoanAmt(
          loanDetails?.vulLoans?.find((x) => x.type === "Fixed")?.amount
        );
      } else if (isOldAlicUlPolicy(data)) {
        setMaxLoanAmt(
          loanDetails?.ulLoans?.find((x) => x.type == "Fixed")?.amount
        );
      }
    });

    getFinancials(data).then((financials) => {
      setCashValue(financials.cashValues[0]);
    });
  }, [data?.contractID]);

  return (
    <CardWithFields
      cardTitle={t("CustomerPolicyDetails-policyValues-title")}
      cardFields={[
        {
          title: (
            <div className="flex items-center">
              {t("CustomerPolicyDetails-policyValues-deathBenefit")}

              <Tooltip
                content={t(
                  "CustomerPolicyDetails-policyValues-deathBenefitTooltip"
                )}
                xMargin="mx-1"
              />
            </div>
          ),
          value: stringFormattingToCurrency(cashValue?.deathBenefit),
        },
        {
          title: t("CustomerPolicyDetails-policyValues-maxLoanAvailable"),
          value: stringFormattingToCurrency(maxLoanAmt), // GetLoan API - wlLoans - amount
        },
        {
          title: (
            <div className="flex items-center">
              {t("CustomerPolicyDetails-policyValues-loanBalance")}
              <Tooltip
                content={
                  <Trans
                    i18nKey="CustomerPolicyDetails-policyValues-loanBalanceTooltip"
                    components={{
                      button: (
                        <button
                          className="ui-btn-hyperlink"
                          onClick={() => setContactUsDialogState(true)}
                        />
                      ),
                    }}
                  />
                }
                xMargin="mx-1"
              />
              <ContactUsDialog
                dialogState={contactUsDialogState}
                setDialogState={setContactUsDialogState}
              />
            </div>
          ),
          value: stringFormattingToCurrency(
            loans?.allProducts?.loanBalance ?? 0
          ), // GetLoan API - wlLoans - loanBalance
        },
        {
          title: t("CustomerPolicyDetails-policyValues-cashValue"),
          value: stringFormattingToCurrency(cashValue?.accountValue),
        },
        {
          title: t("CustomerPolicyDetails-policyValues-surrenderCharge"),
          value: stringFormattingToCurrency(cashValue?.surrenderCharge),
          hidden: parseFloat(cashValue?.surrenderCharge) === 0,
        },
        {
          title: (
            <div className="flex items-center">
              {t("CustomerPolicyDetails-policyValues-cashSurrenderValue")}
              <Tooltip
                content={t(
                  "CustomerPolicyDetails-policyValues-cashSurrenderValueTooltip"
                )}
                xMargin="mx-1"
              />
              <ContactUsDialog
                dialogState={contactUsDialogState}
                setDialogState={setContactUsDialogState}
              />
            </div>
          ),
          value: stringFormattingToCurrency(cashValue?.estimatedSurrenderValue),
        },
      ]}
      additionalContent={
        <CardDisclaimerBase
          disclaimer={
            <>
              {t("CustomerPolicyDetails-policyValues-disclaimer", {
                date: isDeferredAnnuityPlanCodeExists
                  ? moment().add(-1, "days").format("MM/DD/YYYY")
                  : moment().format("MM/DD/YYYY"),
              })}
              <br />
              <Trans
                i18nKey="CustomerPolicyDetails-policyValues-disclaimer-has-max-loan"
                components={{
                  div1: <div className="pt-1" />,
                }}
              />
            </>
          }
        />
      }
    />
  );
}
